/**
 * Admin-only TradingView-style (Lightweight Charts) view of ONE TEK_RSI_MOMENTUM trade:
 * the stored NIFTY 5-minute candles with the recorded entry / signal / exit / partial events.
 *
 * READ-ONLY and additive: it reuses the existing trade-review endpoint
 *   GET /admin/strategies/rsi-momentum/trade-review/trades/{position_id}
 * (ADMIN role enforced server-side by the router; the page that mounts this is admin-routed too)
 * and sits BESIDE the existing Chart.js review charts, which are unchanged. It never fetches
 * market data of its own, recomputes a signal, or places/modifies an order.
 *
 * `initialReview` lets the review page share the response it has already loaded (no second request).
 */
import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import CandlestickVolumeChart from "@/components/charts/CandlestickVolumeChart";
import { IST_ZONE } from "@/components/charts/zonedTime";
import rsiTradeReviewService from "@/services/rsiTradeReviewService";
import type { RsiTradeReview } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { buildTradeReviewLwData, MARKER_COLORS } from "./tradeReviewLightweightData";

export interface TradeReviewLightweightChartProps {
  positionId: string;
  initialReview?: RsiTradeReview | null;
  height?: number;
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string; retryable: boolean }
  | { status: "ready"; review: RsiTradeReview };

function describeError(err: unknown): { message: string; retryable: boolean } {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  if (status === 401) return { message: "Your session has expired. Sign in again to view this trade.", retryable: false };
  if (status === 403) return { message: "Admin access is required to view TEK_RSI_MOMENTUM trade data.", retryable: false };
  if (status === 404) return { message: "This trade was not found for TEK_RSI_MOMENTUM.", retryable: false };
  return { message: extractErrorMessage(err), retryable: true };
}

export default function TradeReviewLightweightChart({ positionId, initialReview = null, height = 380 }: TradeReviewLightweightChartProps) {
  const shared = initialReview && initialReview.summary.id === positionId ? initialReview : null;
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState>(shared ? { status: "ready", review: shared } : { status: "loading" });

  useEffect(() => {
    if (shared && reloadToken === 0) {
      setState({ status: "ready", review: shared });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading" });
    rsiTradeReviewService
      .getTrade(positionId)
      .then((review) => {
        if (!cancelled) setState({ status: "ready", review });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ status: "error", ...describeError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [positionId, reloadToken, shared]);

  const data = useMemo(() => (state.status === "ready" ? buildTradeReviewLwData(state.review) : null), [state]);
  // Default to the trade (its events +/- 12 candles) so markers are legible; "Full session" shows all 75 candles.
  const [view, setView] = useState<"focus" | "full">("focus");
  useEffect(() => {
    setView("focus");
  }, [data]);

  if (state.status === "loading") {
    return (
      <Box display="flex" justifyContent="center" py={6} role="status" aria-label="Loading trade chart">
        <CircularProgress />
      </Box>
    );
  }
  if (state.status === "error") {
    return (
      <Alert
        severity="error"
        data-testid="lw-trade-error"
        action={
          state.retryable ? (
            <Button color="inherit" size="small" onClick={() => setReloadToken((n) => n + 1)}>
              Retry
            </Button>
          ) : undefined
        }
      >
        {state.message}
      </Alert>
    );
  }
  if (!data || data.candles.length === 0) {
    return (
      <Typography color="text.secondary" data-testid="lw-trade-empty">
        No usable stored NIFTY 5-minute candles for this session, so the chart cannot be drawn (see the data warnings).
      </Typography>
    );
  }

  const { review } = state;
  return (
    <Stack spacing={1} data-testid="lw-trade-chart">
      {data.focusRange && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_e, v: "focus" | "full" | null) => {
            if (v !== null) setView(v);
          }}
          aria-label="Chart range"
        >
          <ToggleButton value="focus" aria-label="Focus trade">Focus trade</ToggleButton>
          <ToggleButton value="full" aria-label="Full session">Full session</ToggleButton>
        </ToggleButtonGroup>
      )}
      <CandlestickVolumeChart
        candles={data.candles}
        volume={data.volume}
        markers={data.markers}
        height={height}
        showVolume={data.hasVolume}
        timeZone={IST_ZONE}
        visibleRange={view === "focus" ? data.focusRange : undefined}
      />
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap data-testid="lw-trade-legend">
        <Typography variant="caption"><span style={{ color: MARKER_COLORS.buy, fontWeight: 700 }}>▲</span> BUY entry (LONG)</Typography>
        <Typography variant="caption"><span style={{ color: MARKER_COLORS.sell, fontWeight: 700 }}>▼</span> SELL entry (SHORT)</Typography>
        <Typography variant="caption"><span style={{ color: MARKER_COLORS.signal, fontWeight: 700 }}>●</span> Signal bar</Typography>
        <Typography variant="caption"><span style={{ color: MARKER_COLORS.blocked, fontWeight: 700 }}>●</span> Blocked signal</Typography>
        <Typography variant="caption"><span style={{ color: MARKER_COLORS.exit, fontWeight: 700 }}>■</span> Exit</Typography>
        <Typography variant="caption"><span style={{ color: MARKER_COLORS.partial, fontWeight: 700 }}>●</span> Partial booking</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" data-testid="lw-trade-caption">
        NIFTY spot, completed 5-minute candles, {review.summary.trade_date}. Times shown in IST (Asia/Kolkata). Markers are the
        algo&apos;s recorded events, not recalculated.
        {!data.hasVolume ? " Spot volume is not available for this session, so no volume is drawn." : ""}
        {data.skippedCandles > 0 ? ` ${data.skippedCandles} missing or invalid candle(s) are left blank, not filled in.` : ""}
        {review.data_quality.source !== "ALGO_CANDLE_1M" ? ` Candle source: ${review.data_quality.source} (not the live feed).` : ""}
      </Typography>
      {data.sessionEndsAtIst && (
        <Alert severity="info" data-testid="lw-trade-partial-session">
          Candles are available only through {data.sessionEndsAtIst} IST (the session was still in progress, or later candles are not stored).
          The trade may still have been open after that.
        </Alert>
      )}
      {data.eventLines.length > 0 && (
        <Box data-testid="lw-trade-events">
          <Typography variant="caption" fontWeight={700}>Recorded events (IST)</Typography>
          {data.eventLines.map((line, i) => (
            <Typography key={i} variant="caption" sx={{ display: "block" }}>{line}</Typography>
          ))}
        </Box>
      )}
      {data.unplacedEvents.length > 0 && (
        <Alert severity="warning" data-testid="lw-trade-unplaced">
          Recorded event(s) not drawn because their candle is missing or outside the session: {data.unplacedEvents.join("; ")}.
        </Alert>
      )}
    </Stack>
  );
}
